import { useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import {
  dataTableFootClass,
  dataTableHeadClass,
  DataTablePanel,
  PageShell,
} from '../../components/DataTablePanel';
import { FilterField, ListFiltersBar } from '../../components/ListFiltersBar';
import { SortableTh, sortItems, useTableSort } from '../../components/SortableTh';
import {
  Button,
  Callout,
  Card,
  ListEmptyState,
  LoadingSpinner,
  SegmentedControl,
  Select,
  XIcon,
} from '../../components/ui';
import { ApiError, api } from '../../services/api';
import { t } from '../../i18n';
import { MoneyTotals } from '../../types/customer';
import { AgingBucket, AgingDrilldown, AgingReport } from '../../types/reports';
import { Office } from '../../types/office';
import { formatDate, formatMoney } from '../../utils/format';
import { ReportExportButtons } from './ReportExportButtons';
import { exportAgingReport, type AgingViewMode } from './exportReports';

type ViewMode = AgingViewMode;

const BUCKETS: AgingBucket[] = [
  'notDue',
  'd1_30',
  'd31_60',
  'd61_90',
  'd90plus',
];

const totalsCellClass = 'sticky bottom-0 bg-surface-muted px-3 py-2';
const totalsNumClass = `${totalsCellClass} text-right tabular-nums`;

function daysSinceLastPayment(value: string | null): number | null {
  if (!value) return null;
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return null;
  const today = new Date();
  const startToday = new Date(
    today.getFullYear(),
    today.getMonth(),
    today.getDate(),
  );
  const startPayment = new Date(
    date.getFullYear(),
    date.getMonth(),
    date.getDate(),
  );
  return Math.max(
    0,
    Math.round((startToday.getTime() - startPayment.getTime()) / 86400000),
  );
}

function pickAmount(totals: MoneyTotals, view: ViewMode): string {
  if (view === 'invoice') return totals.invoice;
  if (view === 'cash') return totals.cash;
  return totals.total;
}

export function AgingPage() {
  const labels = t().reports;
  const [view, setView] = useState<ViewMode>('total');
  const [offices, setOffices] = useState<Office[]>([]);
  const [assignees, setAssignees] = useState<
    Array<{ id: number; name: string; active: boolean }>
  >([]);
  const [officeId, setOfficeId] = useState('');
  const [assignedUserId, setAssignedUserId] = useState('');
  const [report, setReport] = useState<AgingReport | null>(null);
  const [drilldown, setDrilldown] = useState<AgingDrilldown | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const { sort, toggleSort } = useTableSort('customerName');

  const agingGetters = useMemo(
    () => ({
      customerName: (row: AgingReport['data'][number]) => row.customerName,
      officeName: (row: AgingReport['data'][number]) => row.officeName,
      assignedUserName: (row: AgingReport['data'][number]) =>
        row.assignedUserName,
      notDue: (row: AgingReport['data'][number]) =>
        pickAmount(row.buckets.notDue, view),
      d1_30: (row: AgingReport['data'][number]) =>
        pickAmount(row.buckets.d1_30, view),
      d31_60: (row: AgingReport['data'][number]) =>
        pickAmount(row.buckets.d31_60, view),
      d61_90: (row: AgingReport['data'][number]) =>
        pickAmount(row.buckets.d61_90, view),
      d90plus: (row: AgingReport['data'][number]) =>
        pickAmount(row.buckets.d90plus, view),
      total: (row: AgingReport['data'][number]) => pickAmount(row.total, view),
      lastPaymentDate: (row: AgingReport['data'][number]) =>
        row.lastPaymentDate,
      daysSinceLastPayment: (row: AgingReport['data'][number]) =>
        daysSinceLastPayment(row.lastPaymentDate),
      openInvoiceCount: (row: AgingReport['data'][number]) =>
        row.openInvoiceCount,
    }),
    [view],
  );

  const displayRows = useMemo(
    () => (report ? sortItems(report.data, sort, agingGetters) : []),
    [report, sort, agingGetters],
  );

  useEffect(() => {
    void Promise.all([api.getOffices(false), api.getCustomerAssignees()]).then(
      ([officeList, assigneeList]) => {
        setOffices(officeList);
        setAssignees(assigneeList);
      },
    );
  }, []);

  useEffect(() => {
    let cancelled = false;
    async function load() {
      setLoading(true);
      setError(null);
      setDrilldown(null);
      try {
        const data = await api.getAgingReport({
          officeId: officeId || undefined,
          assignedUserId: assignedUserId || undefined,
        });
        if (!cancelled) setReport(data);
      } catch (err) {
        if (!cancelled) {
          setError(err instanceof ApiError ? err.message : t().common.error);
        }
      } finally {
        if (!cancelled) setLoading(false);
      }
    }
    void load();
    return () => {
      cancelled = true;
    };
  }, [officeId, assignedUserId]);

  async function openDrilldown(customerId: number, bucket: AgingBucket | 'all') {
    setError(null);
    try {
      const data = await api.getAgingDrilldown(
        customerId,
        bucket === 'all' ? undefined : bucket,
      );
      setDrilldown(data);
    } catch (err) {
      setError(err instanceof ApiError ? err.message : t().common.error);
    }
  }

  function handleExport(format: 'excel' | 'pdf') {
    if (!report) return;
    exportAgingReport({
      report,
      rows: displayRows,
      view,
      officeName:
        offices.find((office) => String(office.id) === officeId)?.name ??
        t().customers.filterAll,
      assigneeName:
        assignees.find((user) => String(user.id) === assignedUserId)?.name ??
        t().customers.filterAll,
      format,
    });
  }

  return (
    <PageShell>
      <ListFiltersBar
        actions={
          <ReportExportButtons
            disabled={!report}
            onExcel={() => handleExport('excel')}
            onPdf={() => handleExport('pdf')}
          />
        }
      >
        <div className="min-w-0 sm:[grid-column:span_2] lg:[grid-column:span_3]">
          <div className="mb-1 text-xs font-medium text-ink-muted">
            {labels.viewMode}
          </div>
          <SegmentedControl
            value={view}
            onChange={setView}
            size="sm"
            aria-label={labels.viewMode}
            options={[
              { id: 'total', label: labels.viewTotal },
              { id: 'invoice', label: labels.viewInvoice },
              { id: 'cash', label: labels.viewCash },
            ]}
          />
        </div>
        <FilterField label={t().nav.office}>
          <Select
            value={officeId}
            size="sm"
            showClearButton={false}
            options={[
              { value: '', label: t().customers.filterAll },
              ...offices.map((office) => ({
                value: String(office.id),
                label: office.active
                  ? office.name
                  : `${office.name} (${t().customers.inactive})`,
              })),
            ]}
            onChange={(e) => setOfficeId(e.target.value)}
          />
        </FilterField>
        <FilterField label={t().customers.assignedUser}>
          <Select
            value={assignedUserId}
            size="sm"
            showClearButton={false}
            options={[
              { value: '', label: t().customers.filterAll },
              ...assignees.map((user) => ({
                value: String(user.id),
                label: user.active
                  ? user.name
                  : `${user.name} (${t().customers.inactive})`,
              })),
            ]}
            onChange={(e) => setAssignedUserId(e.target.value)}
          />
        </FilterField>
      </ListFiltersBar>

      {error ? (
        <Callout variant="danger" className="shrink-0">
          {error}
        </Callout>
      ) : null}

      <DataTablePanel>
        {loading || !report ? (
          <LoadingSpinner message={t().common.loading} />
        ) : report.data.length === 0 ? (
          <ListEmptyState title={t().common.empty} />
        ) : (
          <table className="w-max min-w-full border-separate border-spacing-0 text-sm">
            <thead className={dataTableHeadClass}>
              <tr>
                <SortableTh
                  sortKey="customerName"
                  sort={sort}
                  onSort={toggleSort}
                  className="sticky left-0 z-30 bg-surface-muted"
                >
                  {t().common.customer}
                </SortableTh>
                <SortableTh
                  sortKey="officeName"
                  sort={sort}
                  onSort={toggleSort}
                >
                  {t().nav.office}
                </SortableTh>
                <SortableTh
                  sortKey="assignedUserName"
                  sort={sort}
                  onSort={toggleSort}
                >
                  {t().customers.assignedUser}
                </SortableTh>
                {BUCKETS.map((bucket) => (
                  <SortableTh
                    key={bucket}
                    sortKey={bucket}
                    sort={sort}
                    onSort={toggleSort}
                    align="right"
                  >
                    {labels.buckets[bucket]}
                  </SortableTh>
                ))}
                <SortableTh
                  sortKey="total"
                  sort={sort}
                  onSort={toggleSort}
                  align="right"
                >
                  {t().common.total}
                </SortableTh>
                <SortableTh
                  sortKey="lastPaymentDate"
                  sort={sort}
                  onSort={toggleSort}
                >
                  {t().dashboard.lastPaymentDate}
                </SortableTh>
                <SortableTh
                  sortKey="daysSinceLastPayment"
                  sort={sort}
                  onSort={toggleSort}
                  align="right"
                >
                  {t().dashboard.daysSinceLastPayment}
                </SortableTh>
                <SortableTh
                  sortKey="openInvoiceCount"
                  sort={sort}
                  onSort={toggleSort}
                  align="right"
                >
                  {t().dashboard.openInvoices}
                </SortableTh>
              </tr>
            </thead>
            <tbody>
              {displayRows.map((row) => (
                <tr key={row.customerId} className="border-t border-line">
                  <td className="sticky left-0 z-10 max-w-[16rem] bg-surface-card px-3 py-2 shadow-[inset_-1px_0_0_0_rgb(var(--bf-line))]">
                    <Link
                      to={`/customers/${row.customerId}`}
                      className="block truncate text-brand hover:underline"
                      title={row.customerName}
                    >
                      {row.customerName}
                    </Link>
                  </td>
                  <td className="whitespace-nowrap px-3 py-2">
                    {row.officeName || '—'}
                  </td>
                  <td className="whitespace-nowrap px-3 py-2">
                    {row.assignedUserName || '—'}
                  </td>
                  {BUCKETS.map((bucket) => {
                    const amount = pickAmount(row.buckets[bucket], view);
                    const hasValue = Number(amount) !== 0;
                    return (
                      <td
                        key={bucket}
                        className="px-3 py-2 text-right tabular-nums"
                      >
                        {hasValue ? (
                          <button
                            type="button"
                            onClick={() =>
                              void openDrilldown(row.customerId, bucket)
                            }
                            className="text-brand hover:underline"
                          >
                            {formatMoney(amount)}
                          </button>
                        ) : (
                          formatMoney(amount)
                        )}
                      </td>
                    );
                  })}
                  <td className="px-3 py-2 text-right tabular-nums font-medium">
                    <button
                      type="button"
                      onClick={() =>
                        void openDrilldown(row.customerId, 'all')
                      }
                      className="text-brand hover:underline"
                    >
                      {formatMoney(pickAmount(row.total, view))}
                    </button>
                  </td>
                  <td className="whitespace-nowrap px-3 py-2">
                    {row.lastPaymentDate ? formatDate(row.lastPaymentDate) : '—'}
                  </td>
                  <td className="px-3 py-2 text-right tabular-nums">
                    {daysSinceLastPayment(row.lastPaymentDate) ?? '—'}
                  </td>
                  <td className="px-3 py-2 text-right tabular-nums">
                    {row.openInvoiceCount}
                  </td>
                </tr>
              ))}
            </tbody>
            <tfoot className={dataTableFootClass}>
              <tr>
                <td className={`${totalsCellClass} sticky left-0 z-30`}>
                  Σύνολα
                </td>
                <td className={totalsCellClass} />
                <td className={totalsCellClass} />
                {BUCKETS.map((bucket) => (
                  <td key={bucket} className={totalsNumClass}>
                    {formatMoney(
                      pickAmount(report.totals.buckets[bucket], view),
                    )}
                  </td>
                ))}
                <td className={totalsNumClass}>
                  {formatMoney(pickAmount(report.totals.total, view))}
                </td>
                <td className={totalsCellClass} />
                <td className={totalsCellClass} />
                <td className={totalsNumClass}>
                  {report.data.reduce(
                    (sum, row) => sum + row.openInvoiceCount,
                    0,
                  )}
                </td>
              </tr>
            </tfoot>
          </table>
        )}
      </DataTablePanel>

      {drilldown ? (
        <AgingDrilldownTable
          drilldown={drilldown}
          view={view}
          onClose={() => setDrilldown(null)}
        />
      ) : null}
    </PageShell>
  );
}

function AgingDrilldownTable({
  drilldown,
  view,
  onClose,
}: {
  drilldown: AgingDrilldown;
  view: ViewMode;
  onClose: () => void;
}) {
  const labels = t().reports;
  const { sort, toggleSort } = useTableSort('invoiceNo');
  const getters = useMemo(
    () => ({
      invoiceNo: (i: AgingDrilldown['invoices'][number]) => i.invoiceNo,
      invoiceDate: (i: AgingDrilldown['invoices'][number]) => i.invoiceDate,
      dueDate: (i: AgingDrilldown['invoices'][number]) => i.dueDate,
      daysOverdue: (i: AgingDrilldown['invoices'][number]) => i.daysOverdue,
      original: (i: AgingDrilldown['invoices'][number]) =>
        pickAmount(i.original, view),
      paid: (i: AgingDrilldown['invoices'][number]) => pickAmount(i.paid, view),
      outstanding: (i: AgingDrilldown['invoices'][number]) =>
        pickAmount(i.outstanding, view),
    }),
    [view],
  );
  const displayInvoices = useMemo(
    () => sortItems(drilldown.invoices, sort, getters),
    [drilldown.invoices, sort, getters],
  );

  return (
    <Card className="shrink-0 p-4">
      <div className="mb-3 flex items-center justify-between">
        <h3 className="text-sm font-semibold">
          {labels.drilldownTitle}: {drilldown.customer.name}
        </h3>
        <Button type="button" variant="ghost" size="sm" onClick={onClose}>
          <XIcon className="h-3.5 w-3.5" />
          Κλείσιμο
        </Button>
      </div>
      <div className="max-h-64 overflow-auto">
        <table className="min-w-full text-sm">
          <thead className={dataTableHeadClass}>
            <tr>
              <SortableTh sortKey="invoiceNo" sort={sort} onSort={toggleSort}>
                {t().invoices.invoiceNo}
              </SortableTh>
              <SortableTh
                sortKey="invoiceDate"
                sort={sort}
                onSort={toggleSort}
              >
                {t().invoices.invoiceDate}
              </SortableTh>
              <SortableTh sortKey="dueDate" sort={sort} onSort={toggleSort}>
                {t().invoices.dueDate}
              </SortableTh>
              <SortableTh
                sortKey="daysOverdue"
                sort={sort}
                onSort={toggleSort}
                align="right"
              >
                {labels.daysOverdue}
              </SortableTh>
              <SortableTh
                sortKey="original"
                sort={sort}
                onSort={toggleSort}
                align="right"
              >
                {labels.originalAmount}
              </SortableTh>
              <SortableTh
                sortKey="paid"
                sort={sort}
                onSort={toggleSort}
                align="right"
              >
                {t().invoices.paid}
              </SortableTh>
              <SortableTh
                sortKey="outstanding"
                sort={sort}
                onSort={toggleSort}
                align="right"
              >
                {t().invoices.outstanding}
              </SortableTh>
            </tr>
          </thead>
          <tbody>
            {displayInvoices.map((invoice) => (
              <tr key={invoice.invoiceId} className="border-t border-line">
                <td className="px-3 py-2">
                  <Link
                    to={`/invoices/${invoice.invoiceId}`}
                    className="text-brand hover:underline"
                  >
                    {invoice.invoiceNo}
                  </Link>
                </td>
                <td className="px-3 py-2">
                  {formatDate(invoice.invoiceDate)}
                </td>
                <td className="px-3 py-2">{formatDate(invoice.dueDate)}</td>
                <td className="px-3 py-2 text-right">{invoice.daysOverdue}</td>
                <td className="px-3 py-2 text-right tabular-nums">
                  {formatMoney(pickAmount(invoice.original, view))}
                </td>
                <td className="px-3 py-2 text-right tabular-nums">
                  {formatMoney(pickAmount(invoice.paid, view))}
                </td>
                <td className="px-3 py-2 text-right tabular-nums font-medium">
                  {formatMoney(pickAmount(invoice.outstanding, view))}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </Card>
  );
}
